import React, { useEffect, useMemo, useState } from "react";
import { toMonthlyTrendView } from "helpers/dashboard/aggregateTrendPointsByMonth";
import {
  sliceWebsiteTrends,
  computeTrend,
  buildTrendHeadline,
} from "helpers/dashboard/sliceWebsiteTrends";

const CHART_WIDTH = 640;
const CHART_HEIGHT = 220;
const PADDING = { top: 16, right: 12, bottom: 28, left: 36 };

const PERIOD_OPTIONS = [
  { days: 90, label: "3 months" },
  { days: 180, label: "6 months" },
  { days: 365, label: "12 months" },
];

const MONTHLY_COMPARISON_NOTE =
  "Each point is the total for that calendar month. Trend compares recent months to earlier months in this period.";

const buildXLabelIndexes = (points) => {
  if (points.length <= 1) return [0];
  if (points.length <= 13) return points.map((_, index) => index);

  const targetLabels = 6;
  const step = Math.floor((points.length - 1) / (targetLabels - 1));
  const indexes = [];

  for (let index = 0; index < targetLabels; index += 1) {
    indexes.push(Math.min(index * step, points.length - 1));
  }

  return [...new Set(indexes)];
};

const buildSparklinePath = (points, width, height) => {
  if (!points?.length) return "";

  const max = Math.max(...points.map((row) => row.value), 1);
  const step = points.length > 1 ? width / (points.length - 1) : 0;

  return points
    .map((row, index) => {
      const x = index * step;
      const y = height - (row.value / max) * height;
      return `${index === 0 ? "M" : "L"} ${x.toFixed(1)} ${y.toFixed(1)}`;
    })
    .join(" ");
};

const buildAreaPath = (points, width, height) => {
  const line = buildSparklinePath(points, width, height);
  if (!line) return "";
  return `${line} L ${width} ${height} L 0 ${height} Z`;
};

const TrendBadge = ({ trend }) => {
  if (trend?.changePercent === null || trend?.changePercent === undefined) {
    return <span className="owner-dashboard__trend-badge owner-dashboard__trend-badge--flat">—</span>;
  }

  const prefix =
    trend.direction === "up" ? "↑" : trend.direction === "down" ? "↓" : "→";
  const signed =
    trend.changePercent > 0 ? `+${trend.changePercent}%` : `${trend.changePercent}%`;

  return (
    <span
      className={`owner-dashboard__trend-badge owner-dashboard__trend-badge--${trend.direction}`}
    >
      {prefix} {signed}
    </span>
  );
};

const formatPeriodLabel = (days) => {
  const match = PERIOD_OPTIONS.find((option) => option.days === days);
  if (match) return match.label;
  if (days >= 365) return "12 months";
  if (days >= 180) return "6 months";
  if (days >= 90) return "3 months";
  return `${days} days`;
};

const VALUE_UNIT_LABELS = {
  sessions: "visits",
  programInterest: "clicks",
  signUpIntent: "clicks",
  homeClassSearch: "searches",
};

const WebsiteTrendsChart = ({ websiteTrends }) => {
  const maxDays = websiteTrends?.days || 365;
  const periodOptions = PERIOD_OPTIONS.filter((option) => option.days <= maxDays);
  const longestPeriod = periodOptions[periodOptions.length - 1]?.days || maxDays;
  const [periodDays, setPeriodDays] = useState(longestPeriod);

  useEffect(() => {
    setPeriodDays(longestPeriod);
  }, [longestPeriod]);

  const visibleTrends = useMemo(() => {
    const sliced = sliceWebsiteTrends(websiteTrends, periodDays);
    const monthly = toMonthlyTrendView(sliced);
    if (!monthly?.series) return monthly;

    const series = monthly.series.map((row) => ({
      ...row,
      trend: computeTrend(row.points),
    }));

    return {
      ...monthly,
      series,
      headline: buildTrendHeadline(series),
      comparisonNote: MONTHLY_COMPARISON_NOTE,
    };
  }, [websiteTrends, periodDays]);

  const series = visibleTrends?.series || [];
  const activeSeries = series.filter((row) => row.total > 0);
  const [selectedId, setSelectedId] = useState(null);
  const [hoveredIndex, setHoveredIndex] = useState(null);
  const spanDays = visibleTrends?.days || periodDays;

  const chartSeries = useMemo(() => {
    if (activeSeries.length === 0) return null;
    if (selectedId) {
      return activeSeries.find((row) => row.id === selectedId) || activeSeries[0];
    }
    return activeSeries[0];
  }, [activeSeries, selectedId]);

  const hasActivity = Boolean(websiteTrends?.hasActivity && activeSeries.length > 0);

  useEffect(() => {
    setHoveredIndex(null);
  }, [periodDays, chartSeries?.id]);

  if (!hasActivity) {
    return (
      <article className="owner-dashboard__panel owner-dashboard__trends">
        <h3 className="owner-dashboard__panel-title">Website trends over time</h3>
        <p className="owner-dashboard__panel-subtitle">
          Monthly totals for up to {formatPeriodLabel(maxDays)} from Google Analytics
        </p>
        <p className="owner-dashboard__usage-note">
          {websiteTrends
            ? "Not enough website activity yet to show a trend chart."
            : "Trend data is not available yet — refresh after the dashboard update is deployed, or restart the local dashboard API."}
        </p>
      </article>
    );
  }

  const plotWidth = CHART_WIDTH - PADDING.left - PADDING.right;
  const plotHeight = CHART_HEIGHT - PADDING.top - PADDING.bottom;
  const maxValue = Math.max(...chartSeries.points.map((row) => row.value), 1);
  const xStep =
    chartSeries.points.length > 1
      ? plotWidth / (chartSeries.points.length - 1)
      : 0;

  const linePoints = chartSeries.points
    .map((row, index) => {
      const x = PADDING.left + index * xStep;
      const y = PADDING.top + plotHeight - (row.value / maxValue) * plotHeight;
      return `${index === 0 ? "M" : "L"} ${x.toFixed(1)} ${y.toFixed(1)}`;
    })
    .join(" ");

  const yTicks = [0, 0.5, 1].map((ratio) => ({
    y: PADDING.top + plotHeight - ratio * plotHeight,
    label: Math.round(maxValue * ratio).toLocaleString(),
  }));

  const xLabelIndexes = buildXLabelIndexes(chartSeries.points);

  const hoveredPoint =
    hoveredIndex !== null ? chartSeries.points[hoveredIndex] : null;
  const hoveredX =
    hoveredIndex !== null ? PADDING.left + hoveredIndex * xStep : 0;
  const hoveredY =
    hoveredPoint !== null
      ? PADDING.top + plotHeight - (hoveredPoint.value / maxValue) * plotHeight
      : 0;
  const valueUnit = VALUE_UNIT_LABELS[chartSeries.id] || "events";

  const tooltipWidth = 132;
  const tooltipHeight = 46;
  const flipTooltipBelow = hoveredY < tooltipHeight + PADDING.top + 6;
  const tooltipX = hoveredPoint
    ? Math.min(
        Math.max(hoveredX - tooltipWidth / 2, 4),
        CHART_WIDTH - tooltipWidth - 4
      )
    : 0;
  const tooltipY = hoveredPoint
    ? flipTooltipBelow
      ? hoveredY + 12
      : hoveredY - tooltipHeight - 10
    : 0;

  return (
    <article className="owner-dashboard__panel owner-dashboard__trends">
      <div className="owner-dashboard__panel-header owner-dashboard__panel-header--compact">
        <div>
          <h3 className="owner-dashboard__panel-title">Website trends over time</h3>
          <p className="owner-dashboard__panel-subtitle">
            {formatPeriodLabel(spanDays)} · monthly totals from Google Analytics
          </p>
        </div>
        {periodOptions.length > 1 && (
          <div
            className="owner-dashboard__trend-periods"
            role="group"
            aria-label="Trend time range"
          >
            {periodOptions.map((option) => (
              <button
                key={option.days}
                type="button"
                className={`owner-dashboard__trend-period${
                  periodDays === option.days
                    ? " owner-dashboard__trend-period--active"
                    : ""
                }`}
                onClick={() => setPeriodDays(option.days)}
                aria-pressed={periodDays === option.days}
              >
                {option.label}
              </button>
            ))}
          </div>
        )}
      </div>

      {visibleTrends.headline && (
        <p className="owner-dashboard__usage-headline">{visibleTrends.headline}</p>
      )}

      <div className="owner-dashboard__trend-cards">
        {activeSeries.map((row) => {
          const isSelected = chartSeries.id === row.id;
          return (
            <button
              key={row.id}
              type="button"
              className={`owner-dashboard__trend-card${
                isSelected ? " owner-dashboard__trend-card--selected" : ""
              }`}
              onClick={() => setSelectedId(row.id)}
              aria-pressed={isSelected}
            >
              <div className="owner-dashboard__trend-card-head">
                <span className="owner-dashboard__trend-card-label">{row.label}</span>
                <TrendBadge trend={row.trend} />
              </div>
              <div className="owner-dashboard__trend-card-total">
                {row.total.toLocaleString()} total
              </div>
              <svg
                className="owner-dashboard__trend-sparkline"
                viewBox="0 0 120 32"
                aria-hidden="true"
              >
                <path
                  d={buildAreaPath(row.points, 120, 32)}
                  fill={`${row.color}22`}
                />
                <path
                  d={buildSparklinePath(row.points, 120, 32)}
                  fill="none"
                  stroke={row.color}
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              <p className="owner-dashboard__trend-card-hint">{row.description}</p>
            </button>
          );
        })}
      </div>

      <div
        className="owner-dashboard__trend-chart-wrap"
        onMouseLeave={() => setHoveredIndex(null)}
      >
        <svg
          className="owner-dashboard__trend-chart"
          viewBox={`0 0 ${CHART_WIDTH} ${CHART_HEIGHT}`}
          role="img"
          aria-label={`${chartSeries.label} monthly trend over ${formatPeriodLabel(spanDays)}`}
        >
          {yTicks.map((tick) => (
            <g key={tick.label}>
              <line
                x1={PADDING.left}
                x2={CHART_WIDTH - PADDING.right}
                y1={tick.y}
                y2={tick.y}
                className="owner-dashboard__trend-gridline"
              />
              <text
                x={PADDING.left - 8}
                y={tick.y + 4}
                textAnchor="end"
                className="owner-dashboard__trend-axis-label"
              >
                {tick.label}
              </text>
            </g>
          ))}

          <path
            d={`${linePoints}`}
            fill="none"
            stroke={chartSeries.color}
            strokeWidth="3"
            strokeLinecap="round"
            strokeLinejoin="round"
          />

          {hoveredPoint && (
            <line
              x1={hoveredX}
              x2={hoveredX}
              y1={PADDING.top}
              y2={PADDING.top + plotHeight}
              className="owner-dashboard__trend-hover-line"
            />
          )}

          {chartSeries.points.map((row, index) => {
            const x = PADDING.left + index * xStep;
            const y =
              PADDING.top + plotHeight - (row.value / maxValue) * plotHeight;
            const isHovered = hoveredIndex === index;

            return (
              <g key={row.date}>
                <circle
                  cx={x}
                  cy={y}
                  r={14}
                  fill="transparent"
                  className="owner-dashboard__trend-hit"
                  onMouseEnter={() => setHoveredIndex(index)}
                  aria-hidden="true"
                />
                <circle
                  cx={x}
                  cy={y}
                  r={isHovered ? 6 : 4}
                  fill={chartSeries.color}
                  stroke="#fff"
                  strokeWidth={isHovered ? 2 : 0}
                  pointerEvents="none"
                />
              </g>
            );
          })}

          {xLabelIndexes.map((index) => {
            const row = chartSeries.points[index];
            if (!row) return null;
            const x = PADDING.left + index * xStep;
            return (
              <text
                key={row.date}
                x={x}
                y={CHART_HEIGHT - 8}
                textAnchor="middle"
                className="owner-dashboard__trend-axis-label"
              >
                {row.label}
              </text>
            );
          })}

          {hoveredPoint && (
            <foreignObject
              x={tooltipX}
              y={tooltipY}
              width={tooltipWidth}
              height={tooltipHeight}
              className="owner-dashboard__trend-tooltip-foreign"
            >
              <div
                xmlns="http://www.w3.org/1999/xhtml"
                className={`owner-dashboard__trend-tooltip owner-dashboard__trend-tooltip--svg${
                  flipTooltipBelow ? " owner-dashboard__trend-tooltip--below" : ""
                }`}
              >
                <span className="owner-dashboard__trend-tooltip-date">
                  {hoveredPoint.label}
                </span>
                <strong className="owner-dashboard__trend-tooltip-value">
                  {hoveredPoint.value.toLocaleString()} {valueUnit}
                </strong>
              </div>
            </foreignObject>
          )}
        </svg>
      </div>

      {visibleTrends.comparisonNote && (
        <p className="owner-dashboard__trend-note">{visibleTrends.comparisonNote}</p>
      )}
    </article>
  );
};

export default WebsiteTrendsChart;
